import React, { useState, useEffect } from 'react';
import { taskDataService } from '../../../services/taskDataService';
import {
  Box,
  Typography,
  Paper,
  styled,
  CircularProgress,
  Divider,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  ToggleButtonGroup,
  ToggleButton,
  Alert,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Avatar,
  LinearProgress
} from '@mui/material';
import { useAuth } from '../../../contexts/AuthContext';

// İkonlar
import GroupsIcon from '@mui/icons-material/Groups';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import PendingIcon from '@mui/icons-material/Pending';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PlayCircleFilledIcon from '@mui/icons-material/PlayCircleFilled';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoIcon from '@mui/icons-material/Info';
import FlagIcon from '@mui/icons-material/Flag';
import PauseIcon from '@mui/icons-material/Pause';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import CloseIcon from '@mui/icons-material/Close';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ImageIcon from '@mui/icons-material/Image';

// Stil tanımlamaları
const TaskCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.2s, box-shadow 0.2s',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[6],
  }
}));

const TaskCardContent = styled(CardContent)({
  flexGrow: 1,
});

const StatusChip = styled(Chip)<{ customcolor?: string }>(({ theme, customcolor }) => ({
  fontWeight: 'bold',
  backgroundColor: customcolor ? `${customcolor}20` : undefined,
  color: customcolor,
  borderColor: customcolor ? `${customcolor}50` : undefined,
  '& .MuiChip-icon': {
    color: 'inherit'
  }
}));

const ViewToggleContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  marginBottom: theme.spacing(2),
}));

// Proje Detayları Modal Stili
const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: theme.spacing(2),
  [theme.breakpoints.up('md')]: {
    minWidth: 800,
  },
}));

// Ekip üyesi avatar stili
const MemberAvatar = styled(Avatar)(({ theme }) => ({
  width: 30,
  height: 30,
  fontSize: '0.875rem',
  marginRight: theme.spacing(1),
  backgroundColor: theme.palette.primary.main,
}));

interface TeamTask {
  id: string;
  name: string;
  description?: string;
  projectId: string;
  projectName: string;
  teamId: string;
  teamName: string;
  status: string;
  priority: string;
  createdAt: number;
  deadline?: number;
  teamMemberId: string;
  teamMemberName?: string;
  isSequential: boolean;
  taskOrder?: number;
  canStart?: boolean;
  groupId?: string;
  groupName?: string;
  branchId?: string;
  branchName?: string;
  lastUpdatedAt?: number;
  lastUpdatedBy?: string;
  lastUpdatedByName?: string;
  imageUrls?: string[];
}

// Ekip arayüzünü ekliyoruz
interface Team {
  id: string;
  name: string;
  createdAt: number;
  description?: string;
  groupId?: string;
  groupName?: string;
  branchId?: string;
  branchName?: string;
  memberCount?: number;
  projectCount?: number;
  managerId?: string;
  managerName?: string;
}

// Proje arayüzü eklenecek
interface TeamProject {
  id: string;
  name: string;
  teamId: string;
  teamName: string;
  createdAt: number;
  status: string;
  isSequential: boolean;
  activeTaskOrder?: number;
  groupId?: string;
  groupName?: string;
  branchId?: string;
  branchName?: string;
  description?: string;
}

interface TeamTasksComponentProps {
  companyId?: string;
  userFullName?: string;
}

const TeamTasksComponent: React.FC<TeamTasksComponentProps> = ({ companyId, userFullName }) => {
  const [tasks, setTasks] = useState<TeamTask[]>([]);
  const [teams, setTeams] = useState<Team[]>([]); // Kullanıcının ekiplerini tutacak state
  const [viewMode, setViewMode] = useState<'card' | 'list'>('list');
  const [viewType, setViewType] = useState<'tasks' | 'teams'>('tasks'); // Ekip/Görev görünümü seçimi
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [notification, setNotification] = useState<{message: string, isError: boolean} | null>(null);
  const { currentUser } = useAuth();
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    taskId: string;
    projectId: string;
    action: 'accept' | 'start' | 'pause' | 'complete';
    title: string;
    message: string;
  }>({
    open: false,
    taskId: '',
    projectId: '',
    action: 'accept',
    title: '',
    message: ''
  });
  const [projectDetailsOpen, setProjectDetailsOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<TeamProject | null>(null);
  const [projectTasks, setProjectTasks] = useState<TeamTask[]>([]);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  // LocalStorage'dan kullanıcı görünüm tercihini yükle
  useEffect(() => {
    const savedViewMode = localStorage.getItem('teamTasksViewMode');
    if (savedViewMode && (savedViewMode === 'card' || savedViewMode === 'list')) {
      setViewMode(savedViewMode as 'card' | 'list');
    }
  }, []);

  // Görünüm modu değişikliği
  const handleViewModeChange = (
    event: React.MouseEvent<HTMLElement>,
    newViewMode: 'card' | 'list' | null
  ) => {
    if (newViewMode !== null) {
      setViewMode(newViewMode);
      // LocalStorage'a kullanıcı tercihini kaydet
      localStorage.setItem('teamTasksViewMode', newViewMode);
    }
  };

  // Görünüm tipi değişikliği (görevler/ekipler)
  const handleViewTypeChange = (
    event: React.MouseEvent<HTMLElement>,
    newViewType: 'tasks' | 'teams' | null
  ) => {
    if (newViewType !== null) {
      setViewType(newViewType);
      setPage(0); // Sayfa numarasını sıfırla
    }
  };

  // Sayfa değişikliği
  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setPage(newPage);
  };

  // Sayfa başına satır sayısı değişikliği
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Öncelik rengini belirle 
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return '#F44336'; // Kırmızı
      case 'medium':
        return '#FF9800'; // Turuncu
      case 'low':
        return '#4CAF50'; // Yeşil
      default:
        return '#9E9E9E'; // Gri
    }
  };
  
  // Öncelik metni
  const getPriorityLabel = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'Yüksek';
      case 'medium':
        return 'Orta';
      case 'low':
        return 'Düşük';
      default:
        return 'Belirtilmemiş';
    }
  };

  // Görev tipini getir
  const getTaskTypeLabel = (isSequential: boolean) => {
    return isSequential ? 'Sıralı Görev' : 'Normal Görev';
  };

  // Görev tipi rengini belirle
  const getTaskTypeColor = (isSequential: boolean) => {
    return isSequential ? '#673AB7' : '#009688'; // Mor: Sıralı, Turkuaz: Normal
  };

  // Tarih formatı
  const formatDate = (timestamp: number): string => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('tr-TR') + ' ' + date.toLocaleTimeString('tr-TR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Durum simgesini ve rengini belirle
  const getStatusInfo = (status: string) => {
    switch (status) {
      case 'pending':
        return {
          label: 'Beklemede',
          icon: <PendingIcon />,
          color: '#9E9E9E'
        };
      case 'accepted':
        return {
          label: 'Kabul Edildi',
          icon: <ThumbUpIcon />,
          color: '#2196F3'
        };
      case 'in_progress':
        return {
          label: 'Devam Ediyor',
          icon: <PlayCircleFilledIcon />,
          color: '#FF9800'
        };
      case 'paused':
        return {
          label: 'Duraklatıldı',
          icon: <PauseIcon />,
          color: '#795548'
        };
      case 'completed':
        return {
          label: 'Tamamlandı',
          icon: <CheckCircleIcon />,
          color: '#4CAF50'
        };
      default:
        return {
          label: 'Bilinmiyor',
          icon: <ErrorOutlineIcon />,
          color: '#9E9E9E'
        };
    }
  };

  // Bildirim gösterme fonksiyonu
  const showNotification = (message: string, isError: boolean = false) => {
    setNotification({ message, isError });
    // 3 saniye sonra bildirimi kaldır
    setTimeout(() => {
      setNotification(null);
    }, isError ? 4000 : 2000);
  };

  // Görev başlatılabilir mi kontrolü
  const canStartTask = async (task: TeamTask): Promise<boolean> => {
    // Eğer görev sıralı değilse veya zaten başladıysa başlatılabilir
    if (!task.isSequential || task.status === 'in_progress') {
      return true;
    }

    // Sıralı görev için kontrol
    if (task.canStart !== undefined) {
      // Eğer canStart bilgisi varsa direkt onu kullan
      return task.canStart;
    }

    // canStart bilgisi yoksa ilk görev mi diye kontrol et (backend ayrıca zorlar)
    return task.taskOrder === 1;
  };

  // Görev durumunu güncelleme — SQL (changeTeamTaskStatus)
  // Sıralı akış ilerlemesi, status geçmişi ve proje tamamlanması backend'de yönetilir.
  const updateTaskStatus = async (projectId: string, taskId: string, newStatus: string) => {
    if (!currentUser || !companyId) {
      showNotification('Kullanıcı veya şirket bilgisi bulunamadı', true);
      return;
    }

    try {
      // Mevcut görevi bul (in_progress'in start mı resume mi olduğunu belirlemek için)
      const current =
        projectTasks.find(t => t.id === taskId) ||
        tasks.find(t => t.id === taskId);

      // newStatus → backend action eşlemesi
      let action: 'accept' | 'start' | 'pause' | 'resume' | 'complete' | 'reopen';
      switch (newStatus) {
        case 'accepted': action = 'accept'; break;
        case 'paused': action = 'pause'; break;
        case 'completed': action = 'complete'; break;
        case 'pending': action = 'reopen'; break;
        case 'in_progress':
        default:
          action = current?.status === 'paused' ? 'resume' : 'start';
          break;
      }

      await taskDataService.changeTeamTaskStatus(Number(projectId), Number(taskId), {
        action,
        byUid: currentUser.uid,
        byName: userFullName || 'Kullanıcı',
      });

      const statusInfo = getStatusInfo(newStatus);
      showNotification(`Görev durumu "${statusInfo.label}" olarak güncellendi`);

      fetchTeamTasks();
    } catch (error: any) {
      console.error('Görev durumu güncellenirken hata:', error);
      const msg = error?.response?.data?.message || 'Görev durumu güncellenirken bir hata oluştu';
      showNotification(msg, true);
    }
  };

  // Görev durumu değiştirme onayı
  const handleConfirmAction = () => {
    const { taskId, projectId, action } = confirmDialog;
    
    let newStatus = '';
    switch (action) {
      case 'accept':
        newStatus = 'accepted';
        break;
      case 'start':
        newStatus = 'in_progress';
        break;
      case 'pause':
        newStatus = 'paused';
        break;
      case 'complete':
        newStatus = 'completed';
        break;
    }
    
    console.log('handleConfirmAction - Görev durumu değiştiriliyor:', { 
      taskId, 
      projectId, 
      action,
      newStatus 
    });
    
    if (newStatus) {
      updateTaskStatus(projectId, taskId, newStatus);
    }
    
    setConfirmDialog(prev => ({ ...prev, open: false }));
  };

  // Dialog'u kapat
  const handleCloseDialog = () => {
    setConfirmDialog(prev => ({ ...prev, open: false }));
  };

  // Görev aksiyonu (kabul et, başlat, tamamla)
  const handleTaskAction = async (task: TeamTask, action: 'accept' | 'start' | 'pause' | 'complete') => {
    console.log('handleTaskAction - Çağrıldı:', { 
      taskId: task.id, 
      projectId: task.projectId, 
      action,
      status: task.status,
      isSequential: task.isSequential,
      canStart: task.canStart 
    });
    
    // Başlatma aksiyonunda sıra kontrolü yap
    if (action === 'start' && task.isSequential) {
      const canStart = await canStartTask(task);
      console.log('canStartTask sonucu:', canStart);
      
      if (!canStart) {
        showNotification('Bu görev sıralı bir görev ve henüz sırası gelmedi', true);
        return;
      }
    }
    
    let title = '';
    let message = '';
    
    switch (action) {
      case 'accept':
        title = 'Görevi Kabul Et';
        message = `"${task.name}" görevini kabul etmek istediğinizden emin misiniz?`;
        break;
      case 'start':
        title = 'Göreve Başla';
        message = `"${task.name}" görevine başlamak istediğinizden emin misiniz?`;
        break;
      case 'pause':
        title = 'Görevi Duraklat';
        message = `"${task.name}" görevini duraklatmak istediğinizden emin misiniz?`;
        break;
      case 'complete':
        title = 'Görevi Tamamla';
        message = `"${task.name}" görevini tamamlamak istediğinizden emin misiniz?`;
        break;
    }
    
    setConfirmDialog({
      open: true,
      taskId: task.id,
      projectId: task.projectId,
      action,
      title,
      message
    });
  };

  // Firebase'den kullanıcının ekip görevlerini yükleme (TEK SEFERLİK)
  const fetchTeamTasks = async () => {
    if (!currentUser) {
      setError('Kullanıcı oturumu bulunamadı');
      setLoading(false);
      return;
    }

    if (!companyId) {
      setError('Şirket bilgisi bulunamadı');
      setLoading(false);
      return;
    }

    console.log('-------------TEAM TASKS COMPONENT-------------');
    console.log('Ekip görevlerini yükleme işlemi başlıyor');
    console.log('currentUser.uid:', currentUser.uid);
    console.log('companyId:', companyId);

    setLoading(true);
    setError(null);

    try {
      const toMs = (iso?: string | null): number => (iso ? new Date(iso).getTime() : 0);
      const parseImgs = (raw?: string | null): string[] => {
        if (!raw) return [];
        try { const p = JSON.parse(raw); return Array.isArray(p) ? p : []; } catch { return []; }
      };

      // Tüm projeler + tüm proje görevleri — SQL (taskDataService)
      const projItems: any[] = [];
      let pg = 1;
      for (;;) {
        const res = await taskDataService.listTeamProjects({ page: pg, pageSize: 200 });
        projItems.push(...res.items);
        if (res.items.length === 0 || projItems.length >= (res.total || 0)) break;
        pg++;
      }
      const projectById = new Map<number, any>(projItems.map((p) => [p.id, p]));

      const allTasks = await taskDataService.listAllTeamProjectTasks();

      const tasksList: TeamTask[] = [];
      allTasks.forEach((task) => {
        if (task.teamMemberUid !== currentUser.uid) return;
        const project = projectById.get(task.teamProjectId);
        if (!project) return;

        tasksList.push({
          id: String(task.id),
          name: task.name || 'İsimsiz Görev',
          description: task.description || undefined,
          projectId: String(project.id),
          projectName: project.name || 'İsimsiz Proje',
          teamId: project.teamGroupId != null ? String(project.teamGroupId) : '',
          teamName: project.teamName || '',
          status: task.status || 'pending',
          priority: task.priority || 'medium',
          createdAt: toMs(task.createdAt) || Date.now(),
          deadline: task.dueDate ? toMs(task.dueDate) : undefined,
          teamMemberId: task.teamMemberUid || '',
          teamMemberName: task.teamMemberName || undefined,
          isSequential: project.isSequential || false,
          taskOrder: task.taskOrder ?? undefined,
          canStart: task.canStart,
          groupId: project.groupId != null ? String(project.groupId) : undefined,
          groupName: project.groupName || '',
          branchId: project.branchId || undefined,
          branchName: project.branchName || '',
          lastUpdatedAt: task.lastUpdatedAt ? toMs(task.lastUpdatedAt) : undefined,
          lastUpdatedBy: task.lastUpdatedByUid || undefined,
          lastUpdatedByName: task.lastUpdatedByName || undefined,
          imageUrls: parseImgs(task.imageUrls),
        });
      });

      tasksList.sort((a, b) => b.createdAt - a.createdAt);
      setTasks(tasksList);
    } catch (error) {
      console.error('Ekip görevleri yüklenirken hata:', error);
      setError('Ekip görevleri yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  // Firebase'den kullanıcının ekiplerini yükleme (TEK SEFERLİK)
  const fetchUserTeams = async () => {
    if (!currentUser) {
      setError('Kullanıcı oturumu bulunamadı');
      setLoading(false);
      return;
    }

    if (!companyId) {
      setError('Şirket bilgisi bulunamadı');
      setLoading(false);
      return;
    }

    console.log('-------------TEAM TASKS COMPONENT-------------');
    console.log('Ekip gruplarını yükleme işlemi başlıyor');
    console.log('currentUser.uid:', currentUser.uid);
    console.log('companyId:', companyId);

    setLoading(true);
    setError(null);

    try {
      const toMs = (iso?: string | null): number => (iso ? new Date(iso).getTime() : 0);

      // Ekip grupları + üyeler + projeler — SQL (taskDataService)
      const [groups, members, projRes] = await Promise.all([
        taskDataService.listTeamGroups(),
        taskDataService.listAllTeamMembers(),
        taskDataService.listTeamProjects({ page: 1, pageSize: 500 }),
      ]);

      // Lider üyeyi grup başına bul
      const managerByGroup = new Map<number, { id: string; name: string }>();
      members.forEach((m) => {
        if (m.memberRole === 'Lider' && !managerByGroup.has(m.teamGroupId)) {
          managerByGroup.set(m.teamGroupId, { id: m.personnelFirebaseUid, name: m.name || '' });
        }
      });

      // Proje sayısı grup başına
      const projectCountByGroup = new Map<number, number>();
      projRes.items.forEach((p) => {
        if (p.teamGroupId == null) return;
        projectCountByGroup.set(p.teamGroupId, (projectCountByGroup.get(p.teamGroupId) || 0) + 1);
      });

      const teamsList: Team[] = groups.map((g) => {
        const mgr = managerByGroup.get(g.id);
        return {
          id: String(g.id),
          name: g.name || 'İsimsiz Ekip',
          createdAt: toMs(g.createdAt) || Date.now(),
          description: g.description || undefined,
          memberCount: g.memberCount || 0,
          projectCount: projectCountByGroup.get(g.id) || 0,
          managerId: mgr?.id || '',
          managerName: mgr?.name || '',
        } as Team;
      });

      teamsList.sort((a, b) => b.createdAt - a.createdAt);
      setTeams(teamsList);
    } catch (error) {
      console.error('Ekip grupları yüklenirken hata:', error);
      setError('Ekip grupları yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  // İlk yükleme
  useEffect(() => {
    if (viewType === 'tasks') {
      fetchTeamTasks();
    } else {
      fetchUserTeams();
    }
  }, [currentUser, companyId, viewType]);

  // Aksiyonları render et
  const renderTaskActions = (task: TeamTask) => {
    const status = task.status;
    
    // Görev tamamlandıysa aksiyonlar gösterme
    if (status === 'completed') {
      return (
        <Chip
          icon={<CheckCircleIcon />}
          label="Tamamlandı"
          size="small"
          color="success"
          variant="outlined"
        />
      );
    }
    
    return (
      <Box sx={{ display: 'flex', gap: 1 }}>
        {status === 'pending' && (
          <Button
            size="small"
            variant="contained"
            color="primary"
            startIcon={<ThumbUpIcon />}
            onClick={() => handleTaskAction(task, 'accept')}
          >
            Kabul Et
          </Button>
        )}
        
        {status === 'accepted' && (
          <Button
            size="small"
            variant="contained"
            color="primary"
            startIcon={<PlayCircleFilledIcon />}
            onClick={() => handleTaskAction(task, 'start')}
            disabled={task.isSequential && !task.canStart}
            title={task.isSequential && !task.canStart ? 'Bu görev sıralı bir görev ve henüz sırası gelmedi' : ''}
          >
            Başlat
          </Button>
        )}
        
        {status === 'in_progress' && (
          <>
            <Button
              size="small"
              variant="outlined"
              color="warning"
              startIcon={<PauseIcon />}
              onClick={() => handleTaskAction(task, 'pause')}
            >
              Duraklat
            </Button>
            
            <Button
              size="small"
              variant="contained"
              color="success"
              startIcon={<CheckCircleIcon />}
              onClick={() => handleTaskAction(task, 'complete')}
            >
              Tamamla
            </Button>
          </>
        )}
        
        {status === 'paused' && (
          <>
            <Button
              size="small"
              variant="contained"
              color="primary"
              startIcon={<PlayCircleFilledIcon />}
              onClick={() => handleTaskAction(task, 'start')}
            >
              Devam Et
            </Button>
            
            <Button
              size="small"
              variant="contained"
              color="success"
              startIcon={<CheckCircleIcon />}
              onClick={() => handleTaskAction(task, 'complete')}
            >
              Tamamla
            </Button>
          </>
        )}
      </Box>
    );
  };

  // Proje detaylarını açma fonksiyonu
  const handleOpenProjectDetails = async (projectId: string) => {
    if (!companyId) return;
    
    try {
      const toMs = (iso?: string | null): number => (iso ? new Date(iso).getTime() : 0);
      const parseImgs = (raw?: string | null): string[] => {
        if (!raw) return [];
        try { const p = JSON.parse(raw); return Array.isArray(p) ? p : []; } catch { return []; }
      };

      // Proje + görevleri — SQL (taskDataService)
      const detail = await taskDataService.getTeamProject(Number(projectId));
      if (detail) {
        const pd = detail.project;
        const project: TeamProject = {
          id: String(pd.id),
          name: pd.name || 'İsimsiz Proje',
          teamId: pd.teamGroupId != null ? String(pd.teamGroupId) : '',
          teamName: pd.teamName || '',
          createdAt: toMs(pd.createdAt),
          status: pd.status || 'active',
          isSequential: pd.isSequential || false,
          activeTaskOrder: pd.activeTaskOrder ?? undefined,
          groupId: pd.groupId != null ? String(pd.groupId) : undefined,
          groupName: pd.groupName || undefined,
          branchId: pd.branchId || undefined,
          branchName: pd.branchName || undefined,
          description: undefined,
        };

        const tasks: TeamTask[] = detail.tasks.map((task) => ({
          id: String(task.id),
          name: task.name || 'İsimsiz Görev',
          description: task.description || undefined,
          projectId: String(pd.id),
          projectName: project.name,
          teamId: project.teamId,
          teamName: project.teamName,
          status: task.status || 'pending',
          priority: task.priority || 'medium',
          createdAt: toMs(task.createdAt),
          deadline: task.dueDate ? toMs(task.dueDate) : undefined,
          teamMemberId: task.teamMemberUid || '',
          teamMemberName: task.teamMemberName || undefined,
          isSequential: project.isSequential,
          taskOrder: task.taskOrder ?? undefined,
          canStart: task.canStart,
          groupId: project.groupId,
          groupName: project.groupName,
          branchId: project.branchId,
          branchName: project.branchName,
          lastUpdatedAt: task.lastUpdatedAt ? toMs(task.lastUpdatedAt) : undefined,
          lastUpdatedBy: task.lastUpdatedByUid || undefined,
          lastUpdatedByName: task.lastUpdatedByName || undefined,
          imageUrls: parseImgs(task.imageUrls),
        }));

        tasks.sort((a, b) => {
          if (a.taskOrder && b.taskOrder) {
            return a.taskOrder - b.taskOrder;
          }
          return 0;
        });

        setSelectedProject(project);
        setProjectTasks(tasks);
        setProjectDetailsOpen(true);
      } else {
        showNotification('Proje bilgileri bulunamadı', true);
      }
    } catch (error) {
      console.error('Proje detayları alınırken hata:', error);
      showNotification('Proje detayları alınırken bir hata oluştu', true);
    }
  };
  
  // Proje detaylarını kapatma fonksiyonu
  const handleCloseProjectDetails = () => {
    setProjectDetailsOpen(false);
    setSelectedProject(null);
    setProjectTasks([]);
  };

  // Ekip detaylarını açma fonksiyonu
  const handleOpenTeamDetails = async (teamId: string) => {
    if (!companyId) return;
    
    try {
      // Ekip grubu + üyeleri — SQL (taskDataService)
      const detail = await taskDataService.getTeamGroup(Number(teamId));

      if (detail) {
        const members = detail.members || [];
        let leaderName = "Belirtilmemiş";
        const totalMembers = members.length;
        let membersList = "";

        members.forEach((m) => {
          if (m.memberRole === 'Lider') leaderName = m.name || 'İsimsiz';
        });

        members.slice(0, 3).forEach((m) => {
          membersList += `${m.name} (${m.duty || 'Görev belirtilmemiş'}), `;
        });
        membersList = membersList.replace(/,\s*$/, "");
        if (totalMembers > 3) {
          membersList += ` ve ${totalMembers - 3} kişi daha...`;
        }

        const detailMessage = `
          Ekip: ${detail.group.name}
          Lider: ${leaderName}
          Toplam Üye: ${totalMembers}
          Üyeler: ${membersList || "Üye bulunamadı"}
        `;

        showNotification(`${detail.group.name} ekip detayları görüntülendi`, false);
        console.log("Ekip detayları:", detailMessage);
      } else {
        showNotification('Ekip bilgileri bulunamadı', true);
      }
    } catch (error) {
      console.error('Ekip detayları alınırken hata:', error);
      showNotification('Ekip detayları alınırken bir hata oluştu', true);
    }
  };

  // Kart görünümü
  const renderCardView = () => {
    return (
      <Grid container spacing={2}>
        {tasks.length === 0 ? (
          <Grid item xs={12}>
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="textSecondary">
                Atanmış ekip görevi bulunamadı.
              </Typography>
            </Paper>
          </Grid>
        ) : (
          tasks
            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            .map((task) => {
              const statusInfo = getStatusInfo(task.status);
              
              return (
                <Grid item xs={12} sm={6} md={4} lg={3} key={task.id}>
                  <TaskCard>
                    <Box sx={{ 
                      p: 0.5, 
                      height: 8, 
                      bgcolor: getPriorityColor(task.priority), 
                      borderTopLeftRadius: 8,
                      borderTopRightRadius: 8
                    }} />
                    <TaskCardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                        <Typography variant="h6" component="div" fontWeight="bold" gutterBottom>
                          {task.name}
                        </Typography>
                        <StatusChip
                          icon={statusInfo.icon}
                          label={statusInfo.label}
                          size="small"
                          customcolor={statusInfo.color}
                          variant="outlined"
                        />
                      </Box>
                      
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 0.5,
                          mb: 1
                        }}>
                          <InfoIcon fontSize="small" />
                          Proje: {task.projectName}
                        </Typography>
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <Avatar 
                            sx={{ 
                              width: 24, 
                              height: 24, 
                              mr: 1, 
                              bgcolor: 'primary.main',
                              fontSize: '0.75rem'
                            }}
                          >
                            <GroupsIcon sx={{ fontSize: '1rem' }} />
                          </Avatar>
                          <Typography variant="body2">
                            {task.teamName}
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Box sx={{ mb: 2 }}>
                        <Chip 
                          label={getTaskTypeLabel(task.isSequential)} 
                          size="small"
                          sx={{ 
                            mb: 1,
                            bgcolor: `${getTaskTypeColor(task.isSequential)}20`,
                            color: getTaskTypeColor(task.isSequential),
                            fontWeight: 'medium'
                          }}
                        />
                        
                        {(task.groupName || task.branchName) && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {task.groupName && `Grup: ${task.groupName}`} 
                            {task.groupName && task.branchName && ' | '}
                            {task.branchName && `Şube: ${task.branchName}`}
                          </Typography>
                        )}
                      </Box>
                      
                      {task.description && (
                        <Box sx={{ mb: 2 }}>
                          <Tooltip title={task.description}>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                fontSize: '0.75rem',
                                whiteSpace: 'normal',
                                wordBreak: 'break-word',
                                maxWidth: '200px',
                                minHeight: '32px',
                                lineHeight: '16px'
                              }}
                            >
                              {task.description}
                            </Typography>
                          </Tooltip>
                        </Box>
                      )}
                      
                      <Divider sx={{ my: 1.5 }} />
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ 
                          display: 'flex', 
                          alignItems: 'center'
                        }}>
                          <AccessTimeIcon fontSize="small" sx={{ mr: 0.5 }} />
                          {formatDate(task.createdAt)}
                        </Typography>
                        
                        <Chip
                          icon={<FlagIcon />}
                          label={getPriorityLabel(task.priority)}
                          size="small"
                          sx={{ 
                            bgcolor: `${getPriorityColor(task.priority)}20`,
                            color: getPriorityColor(task.priority),
                            fontWeight: 'medium'
                          }}
                        />
                      </Box>
                      
                      {task.deadline && (
                        <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                          Son Tarih: {formatDate(task.deadline)}
                        </Typography>
                      )}
                    </TaskCardContent>
                    <CardActions sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between' }}>
                        <Button 
                          size="small" 
                          variant="outlined" 
                          color="primary"
                          startIcon={<InfoIcon />}
                          onClick={() => handleOpenProjectDetails(task.projectId)}
                        >
                          Detaylar
                        </Button>
                        {renderTaskActions(task)}
                      </Box>
                    </CardActions>
                  </TaskCard>
                </Grid>
              );
            })
        )}
      </Grid>
    );
  };

  // Tablo görünümü
  const renderTableView = () => {
    return (
      <TableContainer component={Paper} sx={{ 
        mt: 2, 
        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
        borderRadius: 2,
        overflow: 'auto',
        maxHeight: '60vh',
        '&::-webkit-scrollbar': {
          width: '8px',
          height: '8px',
        },
        '&::-webkit-scrollbar-track': {
          background: '#f1f1f1',
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#888',
          borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb:hover': {
          background: '#555',
        },
      }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>Görev Adı</TableCell>
              <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>Proje</TableCell>
              <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>Ekip</TableCell>
              <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>Durum</TableCell>
              <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>Öncelik</TableCell>
              <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>Görev Tipi</TableCell>
              <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>Oluşturulma</TableCell>
              <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>Son Tarih</TableCell>
              <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold', width: '180px' }}>Açıklama</TableCell>
              <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold', width: '100px' }}>Resimler</TableCell>
              <TableCell align="right" sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>İşlemler</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tasks.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  Atanmış ekip görevi bulunamadı.
                </TableCell>
              </TableRow>
            ) : (
              tasks
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((task) => {
                  const statusInfo = getStatusInfo(task.status);
                  
                  return (
                    <TableRow key={task.id} hover sx={{ '& > td': { py: 1 } }}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {task.name}
                        </Typography>
                        {task.isSequential && task.taskOrder && (
                          <Chip 
                            label={`Sıra: ${task.taskOrder}`} 
                            size="small"
                            sx={{ mt: 0.5, fontSize: '0.7rem' }}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {task.projectName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {task.groupName && `${task.groupName}`}
                          {task.groupName && task.branchName && ' - '}
                          {task.branchName && `${task.branchName}`}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Avatar sx={{ width: 24, height: 24, mr: 1, fontSize: '0.75rem' }}>
                            <GroupsIcon sx={{ fontSize: '1rem' }} />
                          </Avatar>
                          <Typography variant="body2" noWrap>
                            {task.teamName}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <StatusChip
                          icon={statusInfo.icon}
                          label={statusInfo.label}
                          size="small"
                          customcolor={statusInfo.color}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip 
                          icon={<FlagIcon />}
                          label={getPriorityLabel(task.priority)} 
                          size="small"
                          sx={{ 
                            bgcolor: `${getPriorityColor(task.priority)}20`,
                            color: getPriorityColor(task.priority),
                            fontWeight: 'medium'
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={getTaskTypeLabel(task.isSequential)} 
                          size="small"
                          sx={{ 
                            bgcolor: `${getTaskTypeColor(task.isSequential)}20`,
                            color: getTaskTypeColor(task.isSequential),
                            fontWeight: 'medium'
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        {formatDate(task.createdAt)}
                      </TableCell>
                      <TableCell>
                        {task.deadline ? (
                          <Typography variant="body2" color="error">
                            {formatDate(task.deadline)}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Belirtilmemiş
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {task.description ? (
                          <Tooltip title={task.description}>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                fontSize: '0.75rem',
                                whiteSpace: 'normal',
                                wordBreak: 'break-word',
                                maxWidth: '200px',
                                minHeight: '32px',
                                lineHeight: '16px'
                              }}
                            >
                              {task.description}
                            </Typography>
                          </Tooltip>
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', fontSize: '0.75rem' }}>
                            -
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {task.imageUrls && task.imageUrls.length > 0 ? (
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            {task.imageUrls.map((url: string, imgIndex: number) => (
                              <Tooltip key={imgIndex} title="Resmi görüntülemek için tıklayın">
                                <Box
                                  component="img"
                                  src={url}
                                  alt={`Görev Resmi ${imgIndex + 1}`}
                                  sx={{
                                    width: 36,
                                    height: 36,
                                    borderRadius: 1,
                                    border: '1px solid #eee',
                                    cursor: 'pointer'
                                  }}
                                  onClick={() => handleOpenImageDialog(url)}
                                />
                              </Tooltip>
                            ))}
                          </Box>
                        ) : (
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', fontSize: '0.75rem' }}>
                              -
                            </Typography>
                          </Box>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            color="primary"
                            startIcon={<InfoIcon />}
                            onClick={() => handleOpenProjectDetails(task.projectId)}
                          >
                            Detaylar
                          </Button>
                          {renderTaskActions(task)}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  // Ekipler listesi görünümü
  const renderTeamsListView = () => {
    return (
      <TableContainer component={Paper} sx={{ 
        mt: 2, 
        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
        borderRadius: 2,
        overflow: 'auto',
        maxHeight: '60vh',
        '&::-webkit-scrollbar': {
          width: '8px',
          height: '8px',
        },
        '&::-webkit-scrollbar-track': {
          background: '#f1f1f1',
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#888',
          borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb:hover': {
          background: '#555',
        },
      }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>Ekip Adı</TableCell>
              <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>Oluşturulma</TableCell>
              <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>Üye Sayısı</TableCell>
              <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>Ekip Lideri</TableCell>
              <TableCell align="right" sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>İşlemler</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {teams.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  Hiç ekip grubu bulunamadı.
                </TableCell>
              </TableRow>
            ) : (
              teams
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((team) => (
                  <TableRow key={team.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar sx={{ width: 32, height: 32, mr: 1, bgcolor: 'primary.main' }}>
                          <GroupsIcon sx={{ fontSize: '1.2rem' }} />
                        </Avatar>
                        <Typography variant="body2" fontWeight="medium">
                          {team.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{formatDate(team.createdAt)}</TableCell>
                    <TableCell>
                      <Chip 
                        icon={<GroupsIcon />}
                        label={team.memberCount || 0} 
                        size="small"
                        color="secondary" 
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      {team.managerName ? (
                        <Typography variant="body2">{team.managerName}</Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">Belirtilmemiş</Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant="outlined"
                        color="primary"
                        startIcon={<InfoIcon />}
                        onClick={() => handleOpenTeamDetails(team.id)}
                      >
                        Detaylar
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  // Ekipler kart görünümü
  const renderTeamsCardView = () => {
    return (
      <Grid container spacing={2}>
        {teams.length === 0 ? (
          <Grid item xs={12}>
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="textSecondary">
                Hiç ekip grubu bulunamadı.
              </Typography>
            </Paper>
          </Grid>
        ) : (
          teams
            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            .map((team) => (
              <Grid item xs={12} sm={6} md={4} key={team.id}>
                <TaskCard>
                  <Box sx={{ 
                    p: 1.5, 
                    bgcolor: 'primary.main', 
                    color: 'white',
                    borderTopLeftRadius: 8,
                    borderTopRightRadius: 8,
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    <GroupsIcon sx={{ mr: 1.5 }} />
                    <Typography variant="h6" component="div" fontWeight="bold">
                      {team.name}
                    </Typography>
                  </Box>
                  <TaskCardContent>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        mb: 1
                      }}>
                        <AccessTimeIcon fontSize="small" sx={{ mr: 0.5 }} />
                        Oluşturulma: {formatDate(team.createdAt)}
                      </Typography>
                    </Box>
                    
                    {team.description && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" sx={{ 
                          p: 1.5, 
                          bgcolor: '#f5f5f5', 
                          borderRadius: 1,
                          maxHeight: 80,
                          overflow: 'auto'
                        }}>
                          {team.description}
                        </Typography>
                      </Box>
                    )}
                    
                    <Divider sx={{ my: 1.5 }} />
                    
                    <Box sx={{ mb: 1.5 }}>
                      <Typography variant="body2" fontWeight="medium">
                        Üye Sayısı
                      </Typography>
                      <Chip 
                        icon={<GroupsIcon />}
                        label={team.memberCount || 0} 
                        size="small"
                        color="secondary" 
                        variant="outlined"
                        sx={{ mt: 0.5 }}
                      />
                    </Box>
                    
                    {team.managerName && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          Ekip Lideri: <Box component="span" fontWeight="medium">{team.managerName}</Box>
                        </Typography>
                      </Box>
                    )}
                  </TaskCardContent>
                  <CardActions sx={{ p: 2 }}>
                    <Button 
                      fullWidth
                      variant="outlined" 
                      color="primary"
                      startIcon={<InfoIcon />}
                      onClick={() => handleOpenTeamDetails(team.id)}
                    >
                      Ekip Detayları
                    </Button>
                  </CardActions>
                </TaskCard>
              </Grid>
            ))
        )}
      </Grid>
    );
  };
  
  // Ekipler görünümü
  const renderTeamsView = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress size={40} />
        </Box>
      );
    }
    
    if (error) {
      return (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      );
    }
    
    return (
      <>
        {viewMode === 'card' ? renderTeamsCardView() : renderTeamsListView()}
        
        <TablePagination
          rowsPerPageOptions={[25, 50, 100]}
          component="div"
          count={teams.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Sayfa başına satır:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
        />
      </>
    );
  };

  // Resim diyaloğunu açma işlevi
  const handleOpenImageDialog = (imageUrl: string) => {
    setSelectedImage(imageUrl);
    setImageDialogOpen(true);
  };

  // Resim diyaloğunu kapatma işlevi
  const handleCloseImageDialog = () => {
    setImageDialogOpen(false);
    setSelectedImage(null);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress size={40} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <ViewToggleContainer>
        <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between' }}>
          <ToggleButtonGroup
            value={viewType}
            exclusive
            onChange={handleViewTypeChange}
            aria-label="içerik tipi"
            size="small"
          >
            <ToggleButton value="tasks" aria-label="görevler görünümü">
              <Tooltip title="Görevler">
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <AssignmentIcon sx={{ mr: 1 }} />
                  <Typography variant="body2">Görevler</Typography>
                </Box>
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="teams" aria-label="ekipler görünümü">
              <Tooltip title="Ekipler">
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <GroupsIcon sx={{ mr: 1 }} />
                  <Typography variant="body2">Ekipler</Typography>
                </Box>
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>
          
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={handleViewModeChange}
            aria-label="görünüm modu"
            size="small"
          >
            <ToggleButton value="card" aria-label="kart görünümü">
              <Tooltip title="Kart Görünümü">
                <ViewModuleIcon />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="list" aria-label="liste görünümü">
              <Tooltip title="Liste Görünümü">
                <ViewListIcon />
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </ViewToggleContainer>

      {viewType === 'tasks' ? (
        <>
          {viewMode === 'card' ? renderCardView() : renderTableView()}
          
          <TablePagination
            rowsPerPageOptions={[25, 50, 100]}
            component="div"
            count={tasks.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Sayfa başına satır:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
          />
        </>
      ) : (
        // Ekipler görünümü
        renderTeamsView()
      )}

      {/* Bildirim Snackbar'ı */}
      {notification && (
        <Snackbar 
          open={true}
          autoHideDuration={notification.isError ? 4000 : 2000} 
          onClose={() => setNotification(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert 
            onClose={() => setNotification(null)} 
            severity={notification.isError ? "error" : "success"}
            sx={{
              fontWeight: 'bold',
              fontSize: '16px',
              width: '100%'
            }}
          >
            {notification.message}
          </Alert>
        </Snackbar>
      )}

      {/* Onay Dialog'u */}
      <Dialog
        open={confirmDialog.open}
        onClose={handleCloseDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {confirmDialog.title}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" id="alert-dialog-description">
            {confirmDialog.message}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="inherit">
            İptal
          </Button>
          <Button onClick={handleConfirmAction} color="primary" variant="contained" autoFocus>
            Evet
          </Button>
        </DialogActions>
      </Dialog>

      {/* Proje Detayları Modalı */}
      <Dialog
        open={projectDetailsOpen}
        onClose={handleCloseProjectDetails}
        maxWidth="lg"
        fullWidth
      >
        {selectedProject && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">{selectedProject.name} - Ekip Görevi Detayları</Typography>
                <IconButton onClick={handleCloseProjectDetails} size="small">
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            <StyledDialogContent>
              {/* Proje Bilgileri */}
              <Paper sx={{ p: 2, mb: 3 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <GroupsIcon sx={{ mr: 1, color: 'primary.main' }} />
                      <Typography variant="subtitle1" fontWeight="medium">
                        Ekip: {selectedProject.teamName}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <AccessTimeIcon sx={{ mr: 1, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        Oluşturulma: {formatDate(selectedProject.createdAt)}
                      </Typography>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="subtitle2" gutterBottom>
                      Görev Tipi:
                    </Typography>
                    <Chip 
                      label={getTaskTypeLabel(selectedProject.isSequential)} 
                      size="small"
                      sx={{ 
                        bgcolor: `${getTaskTypeColor(selectedProject.isSequential)}20`,
                        color: getTaskTypeColor(selectedProject.isSequential),
                        fontWeight: 'medium'
                      }}
                    />
                    
                    {selectedProject.isSequential && selectedProject.activeTaskOrder && (
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        Aktif Görev Sırası: {selectedProject.activeTaskOrder}
                      </Typography>
                    )}
                  </Grid>
                  
                  <Grid item xs={12} sm={6} md={4}>
                    {(selectedProject.groupName || selectedProject.branchName) && (
                      <Box sx={{ mb: 1 }}>
                        {selectedProject.groupName && (
                          <Typography variant="body2">
                            Grup: {selectedProject.groupName}
                          </Typography>
                        )}
                        {selectedProject.branchName && (
                          <Typography variant="body2">
                            Şube: {selectedProject.branchName}
                          </Typography>
                        )}
                      </Box>
                    )}
                    
                    <Typography variant="body2">
                      Durum: {selectedProject.status === 'completed' ? 'Tamamlandı' : 'Devam Ediyor'}
                    </Typography>
                  </Grid>
                  
                  {selectedProject.description && (
                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="subtitle2" gutterBottom>
                        Açıklama:
                      </Typography>
                      <Typography variant="body2">
                        {selectedProject.description}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Paper>
              
              {/* Görevler Tablosu */}
              <Typography variant="h6" gutterBottom>
                Projedeki Tüm Görevler
              </Typography>
              
              <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {selectedProject.isSequential && (
                        <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>Sıra</TableCell>
                      )}
                      <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>Görev Adı</TableCell>
                      <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>Ekip Üyesi</TableCell>
                      <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>Durum</TableCell>
                      <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>Öncelik</TableCell>
                      <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>Oluşturulma</TableCell>
                      <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>Son Güncelleme</TableCell>
                      <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold', width: '180px' }}>Açıklama</TableCell>
                      <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold', width: '100px' }}>Resimler</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {projectTasks.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={selectedProject.isSequential ? 9 : 8} align="center">
                          Projede görev bulunamadı
                        </TableCell>
                      </TableRow>
                    ) : (
                      projectTasks.map((task) => {
                        const statusInfo = getStatusInfo(task.status);
                        return (
                          <TableRow 
                            key={task.id} 
                            hover
                            sx={{ '& > td': { py: 1 } }}
                          >
                            {selectedProject.isSequential && (
                              <TableCell>
                                <Chip 
                                  label={`#${task.taskOrder}`} 
                                  size="small"
                                  color={task.taskOrder === selectedProject.activeTaskOrder ? "primary" : "default"}
                                  variant={task.taskOrder === selectedProject.activeTaskOrder ? "filled" : "outlined"}
                                />
                              </TableCell>
                            )}
                            <TableCell>
                              <Typography variant="body2" fontWeight="medium">
                                {task.name}
                              </Typography>
                              {task.description && (
                                <Tooltip title={task.description}>
                                  <Box sx={{ display: 'inline-flex', alignItems: 'center', ml: 0.5 }}>
                                    <InfoIcon fontSize="small" color="action" />
                                  </Box>
                                </Tooltip>
                              )}
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <MemberAvatar>
                                  {task.teamMemberName ? task.teamMemberName.charAt(0) : '?'}
                                </MemberAvatar>
                                <Typography variant="body2">
                                  {task.teamMemberName || 'Atanmamış'}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <StatusChip
                                icon={statusInfo.icon}
                                label={statusInfo.label}
                                size="small"
                                customcolor={statusInfo.color}
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>
                              <Chip 
                                icon={<FlagIcon />}
                                label={getPriorityLabel(task.priority)} 
                                size="small"
                                sx={{ 
                                  bgcolor: `${getPriorityColor(task.priority)}20`,
                                  color: getPriorityColor(task.priority),
                                  fontWeight: 'medium'
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              {formatDate(task.createdAt)}
                            </TableCell>
                            <TableCell>
                              {task.lastUpdatedAt ? formatDate(task.lastUpdatedAt) : '-'}
                              {task.lastUpdatedByName && (
                                <Typography variant="caption" display="block" color="text.secondary">
                                  {task.lastUpdatedByName}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              {task.description ? (
                                <Tooltip title={task.description}>
                                  <Typography 
                                    variant="body2" 
                                    sx={{ 
                                      display: '-webkit-box',
                                      WebkitLineClamp: 2,
                                      WebkitBoxOrient: 'vertical',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      fontSize: '0.75rem',
                                      whiteSpace: 'normal',
                                      wordBreak: 'break-word',
                                      maxWidth: '200px',
                                      minHeight: '32px',
                                      lineHeight: '16px'
                                    }}
                                  >
                                    {task.description}
                                  </Typography>
                                </Tooltip>
                              ) : (
                                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', fontSize: '0.75rem' }}>
                                  -
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              {task.imageUrls && task.imageUrls.length > 0 ? (
                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                  {task.imageUrls.map((url: string, imgIndex: number) => (
                                    <Tooltip key={imgIndex} title="Resmi görüntülemek için tıklayın">
                                      <Box
                                        component="img"
                                        src={url}
                                        alt={`Görev Resmi ${imgIndex + 1}`}
                                        sx={{
                                          width: 36,
                                          height: 36,
                                          borderRadius: 1,
                                          border: '1px solid #eee',
                                          cursor: 'pointer'
                                        }}
                                        onClick={() => handleOpenImageDialog(url)}
                                      />
                                    </Tooltip>
                                  ))}
                                </Box>
                              ) : (
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', fontSize: '0.75rem' }}>
                                    -
                                  </Typography>
                                </Box>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              
              {/* Görev İlerleme Özeti */}
              <Box sx={{ mt: 3, p: 2, bgcolor: '#f5f9ff', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Görev İlerleme Durumu:
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="body2">
                      Toplam Görev: {projectTasks.length}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="body2">
                      Tamamlanan: {projectTasks.filter(t => t.status === 'completed').length}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="body2">
                      Devam Eden: {projectTasks.filter(t => t.status === 'in_progress').length}
                    </Typography>
                  </Grid>
                </Grid>
                
                {/* İlerleme Çubuğu */}
                {projectTasks.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                      <Typography variant="body2" sx={{ flexGrow: 1 }}>
                        Tamamlanma Oranı:
                      </Typography>
                      <Typography variant="body2" fontWeight="bold" color="primary">
                        {Math.round((projectTasks.filter(t => t.status === 'completed').length / projectTasks.length) * 100)}%
                      </Typography>
                    </Box>
                    <LinearProgress 
                      variant="determinate" 
                      value={(projectTasks.filter(t => t.status === 'completed').length / projectTasks.length) * 100} 
                      sx={{ height: 8, borderRadius: 4 }}
                    />
                  </Box>
                )}
              </Box>
            </StyledDialogContent>
            <DialogActions>
              <Button onClick={handleCloseProjectDetails} color="primary">
                Kapat
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Resim Görüntüleme Diyaloğu */}
      <Dialog
        open={imageDialogOpen}
        onClose={handleCloseImageDialog}
        maxWidth="md"
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Görev Resmi</Typography>
          <IconButton onClick={handleCloseImageDialog}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedImage && (
            <Box
              component="img"
              src={selectedImage}
              alt="Görev Resmi"
              sx={{
                maxWidth: '100%',
                maxHeight: 'calc(80vh - 64px)',
                objectFit: 'contain',
                display: 'block',
                margin: '0 auto'
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default TeamTasksComponent; 